import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { differenceInSeconds } from 'date-fns';
import { isAConversationRoute } from 'dashboard/helper/routeHelpers';
import {
  addSafeEventListener,
  removeSafeEventListener,
} from 'dashboard/helper/domEvents';
import { safeReloadWindow } from 'dashboard/helper/browserNavigation';
import { logError } from 'dashboard/helper/logger';

const MAX_DISCONNECT_SECONDS = 10800;

class ReconnectService {
  constructor(store, router) {
    this.store = store;
    this.router = router;
    this.disconnectTime = null;
    this.isActive = true;
    this.reconnectEpoch = 0;

    this.setupEventListeners();
  }

  disconnect = () => {
    this.isActive = false;
    this.reconnectEpoch += 1;
    this.removeEventListeners();
  };

  setupEventListeners = () => {
    addSafeEventListener(window, 'online', this.handleOnlineEvent);
    emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.on(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
  };

  removeEventListeners = () => {
    removeSafeEventListener(window, 'online', this.handleOnlineEvent);
    emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.off(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
  };

  getSecondsSinceDisconnect = () =>
    this.disconnectTime
      ? Math.max(differenceInSeconds(new Date(), this.disconnectTime), 0)
      : 0;

  // Force reload if the user is disconnected for more than 3 hours
  handleOnlineEvent = () => {
    if (this.getSecondsSinceDisconnect() >= MAX_DISCONNECT_SECONDS) {
      safeReloadWindow();
    }
  };

  fetchConversations = async ({ fullSync = false } = {}) => {
    await this.store.dispatch('updateChatListFilters', {
      page: null,
      updatedWithin: fullSync ? null : this.getSecondsSinceDisconnect(),
    });
    try {
      await this.store.dispatch('fetchAllConversations');
    } finally {
      // Reset the updatedWithin in the store chat list filter after fetching
      // conversations when the user is reconnected. It must happen even when
      // the fetch rejects: left behind, the flag would turn every later fetch
      // into a merge that never drops rows leaving the current view.
      await this.store.dispatch('updateChatListFilters', {
        updatedWithin: null,
      });
    }
  };

  fetchFilteredOrSavedConversations = async (queryData) => {
    await this.store.dispatch('fetchFilteredConversations', {
      queryData,
      page: 1,
    });
  };

  fetchConversationsOnReconnect = async (options) => {
    const { getAppliedConversationFiltersQuery } = this.store.getters;
    const query = getAppliedConversationFiltersQuery?.payload?.length
      ? getAppliedConversationFiltersQuery
      : null;
    if (query) {
      await this.fetchFilteredOrSavedConversations(query);
    } else {
      await this.fetchConversations(options);
    }
  };

  getCurrentRoute = () =>
    this.router.currentRoute?.value || this.router.currentRoute || {};

  fetchConversationMessagesOnReconnect = async ({ fullSync = false } = {}) => {
    const { conversation_id: conversationId } =
      this.getCurrentRoute().params || {};
    if (!conversationId) return null;

    let result = null;
    let refreshAfter = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      // Full refresh resumes its own page cursor; ordinary catch-up uses the
      // account-scoped tail cursor. Both require sequential requests.
      // eslint-disable-next-line no-await-in-loop
      result = await this.store.dispatch('syncActiveConversationMessages', {
        conversationId: Number(conversationId),
        ...(fullSync ? { fullSync: true, refreshAfter } : {}),
      });
      if (fullSync && result?.status === 'partial') {
        refreshAfter = result.nextCursor;
      }
      if (result?.status !== 'failed' && result?.status !== 'partial') {
        break;
      }
    }
    return result;
  };

  revalidateCaches = async () => {
    const { label, inbox } = await this.store.dispatch('accounts/getCacheKeys');
    await Promise.all([
      this.store.dispatch('labels/revalidate', { newKey: label }),
      this.store.dispatch('inboxes/revalidate', { newKey: inbox }),
    ]);
  };

  handleRouteSpecificFetch = async (options) => {
    const currentRoute = this.getCurrentRoute().name;
    if (isAConversationRoute(currentRoute, true)) {
      await this.fetchConversationsOnReconnect(options);
      return this.fetchConversationMessagesOnReconnect(options);
    }
    return null;
  };

  setConversationLastMessageId = async () => {
    const { conversation_id: conversationId } =
      this.getCurrentRoute().params || {};
    if (conversationId) {
      await this.store.dispatch('setConversationLastMessageId', {
        conversationId: Number(conversationId),
      });
    }
  };

  onDisconnect = () => {
    this.reconnectEpoch += 1;
    this.disconnectTime = new Date();
    this.setConversationLastMessageId();
  };

  onReconnect = async (options) => {
    const reconnectEpoch = this.reconnectEpoch + 1;
    this.reconnectEpoch = reconnectEpoch;
    let recoverySucceeded = true;
    let routeRecoveryResult = null;

    try {
      routeRecoveryResult = await this.handleRouteSpecificFetch(options);
    } catch (error) {
      recoverySucceeded = false;
      logError('[ReconnectService] Route recovery failed', error);
    }

    if (!this.isActive || reconnectEpoch !== this.reconnectEpoch) return;

    try {
      await this.revalidateCaches();
    } catch (error) {
      recoverySucceeded = false;
      logError('[ReconnectService] Cache recovery failed', error);
    }

    const routeRecoveryIncomplete = [
      'failed',
      'partial',
      'stale-account',
      'invalidated',
      'missing-conversation',
      'empty-non-final',
    ].includes(routeRecoveryResult?.status);
    if (routeRecoveryIncomplete) {
      recoverySucceeded = false;
      logError(
        '[ReconnectService] Message recovery remained incomplete',
        routeRecoveryResult
      );
    }

    if (!this.isActive || reconnectEpoch !== this.reconnectEpoch) return;

    // The transport is already connected at this point. Data recovery status
    // only controls whether the UI may display a positive reconnect message.
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED, {
      recoverySucceeded,
    });
  };
}

export default ReconnectService;
